import { BaseResult, Result } from '@hitiq/generic';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { initSchema, DataStore, Predicates } from '@aws-amplify/datastore';
import { Amplify, Hub, HubCapsule } from '@aws-amplify/core';
import { injectable } from 'inversify';
import { LogType, type Logger } from '@hitiq/logger';
import { DataStoreModel } from './DataStoreModel';
import { DataStoreSources } from './DataStoreSources';
import { DataStoreSync } from './DataStoreSync';

@injectable()
export class AmplifyDataStore<Model extends DataStoreModel> {
  onNetworkStatusChanged: Subject<boolean>;
  onDataStoreError: Subject<any>;
  onIsSyncingChanged: Subject<boolean>;
  outboxIsEmpty: BehaviorSubject<boolean>;
  onIsSyncedChanged: Subject<boolean>;
  private models: any;
  private modelTypeMap = new Map<string, string>();
  private dataStoreSources: DataStoreSources<Model>;
  private dataStoreSync: DataStoreSync;

  constructor(
    schema: any,
    typeNameMaps: any[],
    awsConfiguration: any,
    private logger: Logger,
    initialize?: boolean,
  ) {
    this.dataStoreSources = new DataStoreSources();
    this.dataStoreSync = new DataStoreSync();

    this.onNetworkStatusChanged = this.dataStoreSync.onNetworkStatusChanged;
    this.onDataStoreError = this.dataStoreSync.onDataStoreError;
    this.onIsSyncingChanged = this.dataStoreSync.onIsSyncingChanged;
    this.outboxIsEmpty = this.dataStoreSync.outboxIsEmpty;
    this.onIsSyncedChanged = this.dataStoreSync.onIsSyncedChanged;
    this.logger.log(LogType.debug, `Datastore: calling Amplify.configure`);
    Amplify.configure(awsConfiguration);

    if (!this.models) {
      this.models = initSchema(schema);
    }

    if (typeNameMaps) this.setTypeNameMap(typeNameMaps);
    if (initialize) this.startObservingDataEvents();

    this.listenToDataStoreEvents();
    this.listenToDataStoreErrors();
    this.listenToAuthEvents();
  }

  startObservingDataEvents() {
    this.logger.log(
      LogType.debug,
      `Datastore: calling DataStore.observe().subscribe`,
    );
    this.dataStoreSources.observeDataStore();
  }

  private listenToAuthEvents() {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    Hub.listen('auth', async (hubData: any) => {
      const { event, data } = hubData.payload;

      if (event === 'signIn') {
        this.logger.log(
          LogType.debug,
          `Datastore: Hub.listen auth event === 'signIn'`,
        );
        // this.dataStoreSources.observeDataStore();
      }

      if (event === 'signOut') {
        this.logger.log(
          LogType.debug,
          `Datastore: Hub.listen auth event === 'signOut'`,
        );
        // await DataStore.clear();
      }
    });
  }

  private listenToDataStoreEvents() {
    Hub.listen('datastore', (hubData: HubCapsule) => {
      this.dataStoreSync.publishSyncEvent(hubData.payload, this.logger);
    });
  }

  private listenToDataStoreErrors() {
    Amplify.configure({
      DataStore: {
        errorHandler: (err: any) => {
          this.logger.log(LogType.error, `Datastore: error ${err.message}`);
          this.dataStoreSync.dataStoreError = err.message;
        },
      },
    });
  }

  onObjectCreated(typeName: string): Observable<Model> {
    return this.dataStoreSources.onCreated(typeName);
  }
  onObjectUpdated(typeName: string): Observable<Model> {
    return this.dataStoreSources.onUpdated(typeName);
  }
  onObjectDeleted(typeName: string): Observable<Model> {
    return this.dataStoreSources.onDeleted(typeName);
  }

  async startSync(): Promise<Result<boolean>> {
    // TODO: allow syncing of different domains, routes, pages, components, ?. Rather then all if it is too much. and decide what comes first, second, third, etc. (https://docs.amplify.aws/lib/datastore/sync/q/platform/js/#selectively-syncing-a-subset-of-your-data)
    const result = new BaseResult<boolean>();
    try {
      // TODO: figure out why it fails to stop and starting again. Needed to trigger the sync complete hub events.
      // await DataStore.stop();
      this.logger.log(LogType.debug, `Datastore: calling DataStore.start()`);
      await DataStore.start();
      result.success = true;
    } catch (error: any) {
      this.logger.log(
        LogType.debug,
        `Datastore: DataStore.start() error: ${result.error.message}`,
      );
      result.error = error;
      result.success = false;
    }

    return result;
  }

  async createObject(object: Model): Promise<Result<Model>> {
    const result = new BaseResult<Model>();
    // if (object.id) {
    //   result.success = false;
    //   result.error = new Error(
    //     `Object already exists with id ${object.id}, did you mean to update?`,
    //   );
    //   return result;
    // }

    const mappedType = this.modelTypeMap.get(object.modelType);
    if (!mappedType) {
      return this.failedMapTypeResult(object.modelType);
    }

    try {
      const returned = await DataStore.save(
        new this.models[mappedType](object),
      );
      result.success = true;
      result.data = { ...returned };
      return result;
    } catch (error) {
      return this.failedQueryResult(error);
    }
  }

  async updateObject(
    object: Partial<Model> & Required<DataStoreModel>,
  ): Promise<Result<Model>> {
    const result = new BaseResult<Model>();
    if (!object.id) {
      return this.failedIdResult();
    }

    const mappedType = this.modelTypeMap.get(object.modelType as string);
    if (!mappedType) {
      return this.failedMapTypeResult(object.modelType as string);
    }

    try {
      const original = await DataStore.query(
        this.models[mappedType],
        object.id,
      );

      const saveResult = await DataStore.save(
        this.models[mappedType].copyOf(original, (copy: any) => {
          Object.keys(object).forEach((key) => {
            if (key !== 'updatedAt' && key !== 'createdAt' && key !== 'type') {
              // TODO: only update if value has changed https://docs.aws.amazon.com/whitepapers/latest/amplify-datastore-implementation/amplify-datastore-best-practices.html
              try {
                // eslint-disable-next-line no-param-reassign
                copy[key] = (object as any)[key];
              } catch (error) {
                // console.log(`error on key: ${key}`);
              }
            }
          });
        }),
      );

      result.success = true;
      result.data = { ...saveResult };
      return result;
    } catch (error) {
      return this.failedQueryResult(error);
    }
  }

  async deleteObject(object: DataStoreModel): Promise<Result<Model>> {
    const result = new BaseResult<Model>();
    if (!object.id) {
      return this.failedIdResult();
    }

    const mappedType = this.modelTypeMap.get(object.modelType);
    if (!mappedType) {
      return this.failedMapTypeResult(object.modelType);
    }

    let deleteResult;
    try {
      deleteResult = await DataStore.delete(this.models[mappedType], object.id);
      result.success = !!deleteResult.length;
      result.data = deleteResult[0] as Model;
      return result;
    } catch (error) {
      return this.failedQueryResult(error);
    }
  }

  async deleteAll(modelType: string): Promise<any> {
    const internalModelName = this.modelTypeMap.get(modelType);
    if (!internalModelName) {
      return this.failedMapTypeResult(modelType);
    }

    try {
      const internalModel = this.models[internalModelName];
      await DataStore.delete(internalModel, Predicates.ALL);
      const result = new BaseResult<any>();
      result.success = true;
      return result;
    } catch (error) {
      return this.failedQueryResult(error);
    }
  }

  async getAll(modelType: string): Promise<Result<Model[]>> {
    const result = new BaseResult<Model[]>();
    const mappedType = this.modelTypeMap.get(modelType);

    if (!mappedType) {
      return this.failedMapTypeResult(modelType);
    }

    try {
      const query = await DataStore.query(this.models[mappedType]);
      result.success = true;
      result.data = [
        ...query.map((entity: any) => ({
          ...(entity as Model),
        })),
      ];
      return result;
    } catch (error) {
      return this.failedQueryResult(error);
    }
  }

  async clear(): Promise<Result<boolean>> {
    // TODO: create an encrypt method.
    // TODO: allow clearing of different domains, routes, pages, components, ?. Rather then all. So that we can keep unsensitive data and deleting only what we have to by law.
    const result = new BaseResult<boolean>();
    try {
      // await DataStore.stop();
      this.logger.log(LogType.debug, `Datastore: calling DataStore.clear()`);
      const res = await DataStore.clear();
      // await DataStore.start();
      this.logger.log(
        LogType.debug,
        `Datastore: called DataStore.clear(). result: ${res}`,
      );
      result.success = true;
    } catch (error: any) {
      this.logger.log(
        LogType.debug,
        `Datastore: DataStore.clear() error: ${error}`,
      );
      result.error = error;
      result.success = false;
    }

    return result;
  }

  async count(type: string): Promise<Result<number>> {
    const queryResult = await this.getAll(type);
    const result = new BaseResult<number>();
    result.success = queryResult.success;
    result.error = queryResult.error;

    if (!queryResult.success) {
      result.data = 0;
    } else {
      result.data = queryResult.data.length;
    }

    return result;
  }

  private failedMapTypeResult(mapType: string): Result<any> {
    const result = new BaseResult();
    result.success = false;
    result.error = new Error(
      `MapType ${mapType} pair does not exist in the modelTypeMap!`,
    );
    return result;
  }

  private failedQueryResult(error: any): Result<any> {
    const result = new BaseResult();
    result.success = false;
    result.error = error;
    return result;
  }

  private failedIdResult(): Result<any> {
    const result = new BaseResult();
    result.success = false;
    result.error = new Error('Object with id does not exist.');
    return result;
  }

  private setTypeNameMap(typeNameMaps: any[]) {
    if (typeNameMaps) {
      typeNameMaps.forEach((element) => {
        if (this.models[element.amplifyType] === undefined) {
          throw new Error(
            `Bad name mapping: ${element.typeString} maps to non-existent type: ${element.amplifyType}`,
          );
        }
        this.modelTypeMap.set(element.typeString, element.amplifyType);
      });
    }
  }
}
